import {AccountDocument, ContactDocument} from 'common'
import {
  AddContactAPIDocument,
  EditAccountAPIDocument,
  EditContactAPIDocument
} from 'common/apis/types'
import {first} from 'lodash'
import {FirebaseInstances} from './helpers'
import {accountsApi, contactsApi} from 'lib/api'

const getApiErrorResult = (error: any): any => {
  const responseData = error?.response?.data
  const rawMessage =
    responseData?.msg ||
    responseData?.data ||
    error?.body?.msg ||
    error?.body?.data ||
    error?.message
  const message =
    typeof rawMessage === 'string' && rawMessage.trim()
      ? rawMessage
      : 'Something went wrong'

  return {
    error: message,
    errorCode: error?.response?.status || error?.status
  }
}

const isPasswordPolicyError = (error: any): boolean => {
  const errorDetails = [
    error?.response?.data,
    error?.body?.data,
    error?.message
  ]
    .map((value) =>
      typeof value === 'string' ? value : JSON.stringify(value) || ''
    )
    .join(' ')

  return errorDetails.includes('PASSWORD_DOES_NOT_MEET_REQUIREMENTS')
}

export function createUserService(_fb: FirebaseInstances) {
  const createAccount = async (accountData: AccountDocument) => {
    try {
      const result = await accountsApi.create<{msg: string}>(accountData)
      return {
        response: result,
        message: (result as any)?.msg || 'Account created'
      }
    } catch (err: any) {
      return getApiErrorResult(err)
    }
  }

  const deleteAccount = async (accountID: string) => {
    try {
      await accountsApi.delete(accountID)
      return {message: 'Account deleted successfully'}
    } catch (error: any) {
      return getApiErrorResult(error)
    }
  }

  const editAccount = async (
    accountData: EditAccountAPIDocument,
    accountId: string
  ) => {
    try {
      const result = await accountsApi.update<{msg: string}>(
        accountId,
        accountData
      )
      return {
        response: result,
        message: (result as any)?.msg || 'Account updated'
      }
    } catch (err: any) {
      return getApiErrorResult(err)
    }
  }

  const createContact = async (contactData: AddContactAPIDocument) => {
    try {
      const result = await contactsApi.create<{
        id?: string
        firebaseUid?: string
        data?: {contactId?: string}
        msg?: string
      }>(contactData)
      const emailDelivery = (result as any)?.accountCreatedEmailDelivery
      const message =
        emailDelivery?.deliveryStatus === 'failed'
          ? emailDelivery.message ||
            'Contact created, but the password setup email could not be sent.'
          : emailDelivery?.deliveryStatus === 'blocked'
          ? emailDelivery.message ||
            'Contact created. Password setup email was skipped by safe outbound email policy.'
          : (result as any)?.msg || 'Contact created'
      return {response: result, message}
    } catch (err: any) {
      if (isPasswordPolicyError(err)) {
        return {
          error: 'Unable to create the contact. Please try again.',
          errorCode: err?.response?.status || err?.status
        }
      }
      return getApiErrorResult(err)
    }
  }

  const deleteContact = async (contactID: string) => {
    try {
      await contactsApi.delete(contactID)
      return {message: 'Contact deleted successfully'}
    } catch (error: any) {
      return getApiErrorResult(error)
    }
  }

  const editContact = async (
    contactData: EditContactAPIDocument,
    contactID: string
  ) => {
    try {
      const result = await contactsApi.update<{msg: string}>(
        contactID,
        contactData
      )
      return {
        response: result,
        message: (result as any)?.msg || 'Contact updated'
      }
    } catch (err: any) {
      return getApiErrorResult(err)
    }
  }

  const updateContactStatus = async (contactId: string, isActive: boolean) => {
    try {
      const result = await contactsApi.updateStatus<{msg: string}>(
        contactId,
        isActive
      )
      return {
        response: result,
        message: (result as any)?.msg || 'Status updated'
      }
    } catch (err: any) {
      return getApiErrorResult(err)
    }
  }

  const getPrimaryContact = async (
    accountId: string
  ): Promise<ContactDocument | undefined> => {
    try {
      const result = await contactsApi.list<{data: ContactDocument[]}>({
        accountId,
        isPrimaryContact: 'true'
      })
      const contacts = (result as any)?.data || result
      return first(Array.isArray(contacts) ? contacts : [])
    } catch (error) {
      return
    }
  }

  return {
    createAccount,
    deleteAccount,
    editAccount,
    createContact,
    deleteContact,
    editContact,
    updateContactStatus,
    getPrimaryContact
  }
}
